import { ReactNode, useEffect, useState, CSSProperties } from "react";
import { Icons } from "./icons";

type BtnProps = {
  kind?: "primary" | "secondary" | "ghost" | "danger";
  size?: "sm";
  children?: ReactNode;
  leading?: ReactNode;
  trailing?: ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  type?: "button" | "submit" | "reset";
};

export const Btn = ({
  kind = "ghost",
  size,
  children,
  leading,
  trailing,
  onClick,
  disabled,
  type = "button",
}: BtnProps) => (
  <button
    className={`ad-btn ad-btn-${kind}${size === "sm" ? " ad-btn-sm" : ""}`}
    onClick={onClick}
    disabled={disabled}
    type={type}
  >
    {leading}
    <span>{children}</span>
    {trailing}
  </button>
);

type PillProps = {
  // Matches the CSS classes in styles.css: ad-pill-{muted,orange,success,error,warn,live,test}
  kind?: "muted" | "orange" | "success" | "error" | "warn" | "live" | "test" | string;
  children?: ReactNode;
  dot?: boolean;
};

export const Pill = ({ kind = "muted", children, dot }: PillProps) => (
  <span className={`ad-pill ad-pill-${kind}`}>
    {dot && <span className="dot" />}
    {children}
  </span>
);

export const PersonaPill = ({ persona }: { persona: "agent" | "human" | string }) =>
  persona === "agent" ? (
    <Pill kind="muted">AI agent</Pill>
  ) : (
    <Pill kind="orange">Person</Pill>
  );

export const LivenessPill = ({ state }: { state: import("./liveness").LivenessDisplayState }) => {
  if (state === "not-run") return <span style={{ color: "var(--ad-fg-4)" }}>—</span>;
  if (state === "passed") return <Pill kind="success" dot>Pass</Pill>;
  return <Pill kind="error" dot>{state === "failed-not-blocking" ? "Failed, let through" : "Failed, blocked"}</Pill>;
};

export const ResultPill = ({ result }: { result: string }) => {
  if (result === "success") return <Pill kind="success" dot>Verified</Pill>;
  if (result === "fail-factor") return <Pill kind="error" dot>Failed</Pill>;
  if (result === "fail-rate") return <Pill kind="warn" dot>Too many tries</Pill>;
  return (
    <Pill kind="muted" dot>
      {result}
    </Pill>
  );
};

export const FactorChip = ({ name }: { name: string }) => {
  const map: Record<string, { label: string; I: typeof Icons[keyof typeof Icons] }> = {
    passkey: { label: "Passkey", I: Icons.Key },
    image_points: { label: "Picture spots", I: Icons.Memory },
    behavior_motion: { label: "Motion", I: Icons.Lightning },
    // Legacy alias still present in older audit metadata.
    questions: { label: "Questions", I: Icons.Memory },
  };
  const it = map[name] || { label: name, I: Icons.Dot };
  return (
    <span
      className="ad-pill ad-pill-orange"
      style={{
        background: "transparent",
        border: "1px solid var(--ad-border-1)",
        color: "var(--ad-fg-2)",
        fontWeight: 500,
      }}
    >
      <it.I size={11} />
      {it.label}
    </span>
  );
};

export function CopyBtn({
  text,
  label = "Copy",
  small,
}: {
  text: string;
  label?: string;
  small?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const onCopy = () => {
    try {
      navigator.clipboard.writeText(text);
    } catch {
      /* ignore */
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };
  return (
    <button
      className={`ad-copy-btn${copied ? " is-copied" : ""}`}
      onClick={onCopy}
      style={small ? { padding: "2px 6px", fontSize: 10 } : undefined}
    >
      {copied ? <Icons.Check /> : <Icons.Copy />}
      {copied ? "Copied" : label}
    </button>
  );
}

type ModalProps = {
  open: boolean;
  onClose?: () => void;
  title?: ReactNode;
  eyebrow?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  width?: number;
};

export function Modal({ open, onClose, title, eyebrow, children, footer, width = 640 }: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  const style: CSSProperties = { maxWidth: width };
  return (
    <div className="ad-modal-shroud" onClick={onClose}>
      <div className="ad-modal" style={style} onClick={(e) => e.stopPropagation()}>
        <div className="ad-modal-head">
          <div className="ad-stack-sm" style={{ flex: 1 }}>
            {eyebrow && <div className="ad-eyebrow">{eyebrow}</div>}
            <h2 className="ad-h2">{title}</h2>
          </div>
          <button
            className="ad-top-icon-btn"
            onClick={onClose}
            style={{ flexShrink: 0 }}
            aria-label="Close"
          >
            <Icons.X />
          </button>
        </div>
        <div className="ad-modal-body">{children}</div>
        {footer && <div className="ad-modal-foot">{footer}</div>}
      </div>
    </div>
  );
}
